/**
 * Module dependencies.
 */

import { AuthField, AuthShell, AuthSubmit } from 'app/components/pages/auth/auth-shell';
import { Logo } from 'app/components/pages/landing/brand';
import { PasswordRequirements, allPasswordRulesMet } from 'app/components/pages/auth/password-requirements';
import { XCircle } from 'lucide-react';
import { appRoutes } from 'app/routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useResetPassword } from 'app/hooks/requests/use-reset-password';
import React, { useState } from 'react';

/**
 * Export `ResetPasswordPage` component.
 */

export function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');

  const { isLoading, mutate: reset } = useResetPassword({
    onError: () => setError('This reset link is invalid or has expired. Request a new one.'),
    onSuccess: () => navigate(appRoutes.dashboard.home.base)
  });

  const allRulesMet = allPasswordRulesMet(password);
  const passwordsMatch = confirm.length > 0 && confirm === password;
  const canSubmit = allRulesMet && passwordsMatch && !isLoading;

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError('');

    if (!allRulesMet) {
      setError('Please meet all password requirements.');

      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match.');

      return;
    }

    reset({ password, token });
  }

  if (!token) {
    return (
      <AuthShell>
        <div className={'flex justify-center'}>
          <Logo size={40} />
        </div>

        <div className={'mx-auto mt-8 grid h-14 w-14 place-items-center rounded-2xl bg-neutral-100'}>
          <XCircle className={'h-7 w-7'} style={{ color: '#BE123C' }} />
        </div>

        <h1 className={"mt-6 font-['Outfit'] text-2xl font-semibold tracking-[-0.03em]"}>{'Invalid link'}</h1>

        <p className={'mx-auto mt-2 max-w-xs text-sm text-neutral-500'}>
          {'This reset link is missing or invalid. Request a new one from the sign-in page.'}
        </p>

        <button
          className={
            'mt-8 h-11 w-full rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50'
          }
          onClick={() => navigate(appRoutes.login)}
          type={'button'}
        >
          {'Back to sign in'}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className={'flex justify-center'}>
        <Logo size={40} />
      </div>

      <h1 className={"mt-8 font-['Outfit'] text-3xl font-semibold tracking-[-0.03em]"}>{'Choose a new password'}</h1>

      <p className={'mx-auto mt-2 max-w-xs text-sm text-neutral-500'}>{'Set a new password for your account.'}</p>

      <form className={'mt-8 space-y-4'} onSubmit={onSubmit}>
        <AuthField
          autoComplete={'new-password'}
          label={'New password'}
          onChange={setPassword}
          type={'password'}
          value={password}
        />

        <PasswordRequirements value={password} />

        <AuthField
          autoComplete={'new-password'}
          label={'Confirm password'}
          onChange={setConfirm}
          type={'password'}
          value={confirm}
        />

        {confirm.length > 0 && !passwordsMatch && (
          <p className={'text-left text-xs text-destructive'}>{'Passwords do not match.'}</p>
        )}

        {error && <p className={'text-left text-sm text-destructive'}>{error}</p>}

        <AuthSubmit disabled={!canSubmit}>{isLoading ? 'Saving…' : 'Reset password'}</AuthSubmit>
      </form>
    </AuthShell>
  );
}
